'use client';

import type { GA4Item } from '@lib/analytics/types';
import { fetchMoreProducts } from '@lib/data/products';
import { isPriceFiltered } from '@lib/util/filter-products';
import type { SortOptions } from '@lib/util/sort-products';
import { toPriceOrder } from '@lib/util/sort-products';
import { toListingVMs } from '@lib/util/to-listing-vms';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductCardVM } from 'types/view-models';

const PAGE_SIZE = PRODUCTS_PAGE_SIZE;

export type UseProductListingParams = {
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
  /** 1-based current page. */
  page: number;
  countryCode: string;
  categoryId?: string;
  collectionId?: string;
  searchQuery?: string;
  listId?: string;
  listName?: string;
  /** SSR-rendered default view (`popular`, no price filter, page 1) — reused
   * as-is when the resolved params match the default, so the common case
   * never triggers an extra client fetch. */
  defaultProducts: ProductCardVM[];
  defaultGa4Items: GA4Item[];
  defaultCount: number;
};

type ListingPage = {
  cards: ProductCardVM[];
  ga4Items: GA4Item[];
  count: number;
};

// "Most Popular" ranks via the best-seller endpoint, which only supports
// category (or global) scoping — collection/search listings fall back to the
// standard endpoint even for the default sort. Mirrors `isPopularSorted` in
// `src/modules/store/templates/paginated-products.tsx`.
function usesPopularEndpoint(
  sortBy: SortOptions,
  options: { searchQuery?: string; collectionId?: string },
): boolean {
  return sortBy === 'popular' && !options.searchQuery && !options.collectionId;
}

function resolveListingStrategy(
  sortBy: SortOptions,
  minPrice: number | undefined,
  maxPrice: number | undefined,
  page: number,
  options: { searchQuery?: string; collectionId?: string },
) {
  const priceFiltered = isPriceFiltered(minPrice, maxPrice);
  const explicitPriceSort = sortBy === 'price_asc' || sortBy === 'price_desc';

  return {
    isDefault: sortBy === 'popular' && !priceFiltered && page === 1,
    priceOrder: explicitPriceSort ? toPriceOrder(sortBy) : undefined,
    // A price-filtered listing routes through the sorted-by-price endpoint
    // for ANY sort (fetchMoreProducts keys on minPrice/maxPrice — MWP-1189),
    // so the best-seller endpoint must not claim it.
    popularOrder:
      !priceFiltered && usesPopularEndpoint(sortBy, options)
        ? ('desc' as const)
        : undefined,
  };
}

// Fetches one page (offset derived from the 1-based `page`) via
// `fetchMoreProducts` — already handles best-seller/price-sorted/standard
// listings identically to the SSR templates, including server-side price
// filtering for any sort (MWP-1189).
async function fetchListingPage(
  params: Pick<
    UseProductListingParams,
    | 'sortBy'
    | 'minPrice'
    | 'maxPrice'
    | 'page'
    | 'countryCode'
    | 'categoryId'
    | 'collectionId'
    | 'searchQuery'
    | 'listId'
    | 'listName'
  > & {
    priceOrder?: 'asc' | 'desc';
    popularOrder?: 'asc' | 'desc';
  },
): Promise<ListingPage> {
  const offset = (params.page - 1) * PAGE_SIZE;
  const result = await fetchMoreProducts({
    offset,
    limit: PAGE_SIZE,
    countryCode: params.countryCode,
    sortBy: params.sortBy,
    categoryId: params.categoryId,
    collectionId: params.collectionId,
    searchQuery: params.searchQuery,
    priceOrder: params.priceOrder,
    popularOrder: params.popularOrder,
    minPrice: params.minPrice,
    maxPrice: params.maxPrice,
  });

  const { cards, ga4Items } = toListingVMs(result.products, {
    listId: params.listId,
    listName: params.listName,
  });

  return { cards, ga4Items, count: result.count };
}

export function useProductListing({
  sortBy,
  minPrice,
  maxPrice,
  page,
  countryCode,
  categoryId,
  collectionId,
  searchQuery,
  listId,
  listName,
  defaultProducts,
  defaultGa4Items,
  defaultCount,
}: UseProductListingParams) {
  const { isDefault, priceOrder, popularOrder } = resolveListingStrategy(
    sortBy,
    minPrice,
    maxPrice,
    page,
    { searchQuery, collectionId },
  );

  const [products, setProducts] = useState<ProductCardVM[]>(
    isDefault ? defaultProducts : [],
  );
  const [ga4Items, setGa4Items] = useState<GA4Item[]>(
    isDefault ? defaultGa4Items : [],
  );
  const [count, setCount] = useState(isDefault ? defaultCount : 0);
  const [isOverriding, setIsOverriding] = useState(!isDefault);
  const [isError, setIsError] = useState(false);
  // Bumped by `retry` to re-run the fetch effect for the same params after a
  // failure — part of the effect's query identity via `paramsKey`.
  const [retryToken, setRetryToken] = useState(0);
  const queryVersionRef = useRef(0);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  const paramsKey = JSON.stringify({
    sortBy,
    minPrice,
    maxPrice,
    page,
    categoryId,
    collectionId,
    searchQuery,
    retryToken,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey is the serialised query identity for this effect; defaultProducts/defaultCount are only read for the default branch
  useEffect(() => {
    queryVersionRef.current += 1;
    const version = queryVersionRef.current;

    if (isDefault) {
      setProducts(defaultProducts);
      setGa4Items(defaultGa4Items);
      setCount(defaultCount);
      setIsOverriding(false);
      setIsError(false);
      return;
    }

    setProducts([]);
    setGa4Items([]);
    setCount(0);
    setIsOverriding(true);
    setIsError(false);

    fetchListingPage({
      sortBy,
      minPrice,
      maxPrice,
      page,
      countryCode,
      categoryId,
      collectionId,
      searchQuery,
      listId,
      listName,
      priceOrder,
      popularOrder,
    })
      .then((result) => {
        if (queryVersionRef.current !== version) return;
        setProducts(result.cards);
        setGa4Items(result.ga4Items);
        setCount(result.count);
        setIsOverriding(false);
      })
      .catch(() => {
        if (queryVersionRef.current !== version) return;
        setIsOverriding(false);
        setIsError(true);
      });
  }, [paramsKey, isDefault]);

  return { products, ga4Items, count, isOverriding, isError, retry };
}
